import { sentryService } from '@/services/telemetry/sentry';
import { resolvePortraitOrientation } from '@/shared/lib/portrait-orientation';
import { reactive } from '@/shared/lib/reactive';
import colorToRgba from '@/shared/utils/color-to-rgba';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import getFileNameFromUrl from '@/shared/utils/get-file-name-from-url';
import setCssVariable from '@/shared/utils/set-css-variable';

/**
 * Complete list of all CSS custom properties set by KioskDesign.
 * Used by clearCssVariables() to remove stale values on reset/re-initialize.
 * IMPORTANT: When adding a new setCssVariable() call in this file,
 * add the property name to this list.
 * @type {string[]}
 */
const ALL_KIOSK_CSS_VARIABLES = [
  '--Kiosk--font_size',
  '--Kiosk--font_color',
  '--Kiosk--font_color-rgb',
  '--Kiosk--font_shadow_size',
  '--Kiosk--font_shadow_opacity',
  '--Kiosk--logo_size',
  '--Kiosk--logo_offset_x',
  '--Kiosk--logo_offset_y',
  '--Kiosk--logo_url',
  '--Kiosk--button_background_color',
  '--Kiosk--button_font_color',
  '--Kiosk--button_rounding',
  '--Kiosk--popup_background_color',
  '--Kiosk--popup_font_color',
  '--Kiosk--background_color',
  '--Kiosk--background_color-rgb',
  '--Kiosk--background_url',
  '--Kiosk--tint-color',
  '--Kiosk--tint-color-rgb',
  '--Kiosk--tint-inverse-color',
  '--Kiosk--tint-inverse-color-rgb',
  '--Kiosk--tint-inverse_color',
  '--Kiosk--tint-inverse_color-rgb',
  '--Kiosk--font_headers_url',
  '--Kiosk--font_regular_url',
  '--Kiosk--portrait_no_photo_icon_size',
  '--Kiosk--font_capitalize',
];
const HORIZONTAL_PORTRAITS_CLASS = 'App--horizontal-portraits';
const PORTRAIT_ORIENTATION_ATTRIBUTE = 'data-kiosk-portrait-orientation';

/**
 * @typedef {Object} KioskPolicyContent
 * @property {string} [popup_text]
 * @property {string} [privacy_text]
 */

/**
 * @typedef {Object} Kiosk
 * @property {string}   slug - URL pathname for the kiosk e.g. "remastered". in https://production.theartisan.ai/remastered
 * @property {string}   printer - Printer ID from the kiosk_printers table, e.g., 'minis_98_canonG550_luster_singleside'.
 * @property {string}   client_name - Name of the client's organization (displayed in footer).
 * @property {string}   partner - Partner ID from the kiosk_partners table
 *
 * @property {string}   logo_url - URL to the client logo image.
 * @property {number}   logo_size - Relative size for the client logo.
 * @property {number}   logo_offset_x - Horizontal offset for the logo position.
 * @property {number}   logo_offset_y - Vertical offset for the logo position.
 *
 * @property {string}   background_color - Hex color code for the kiosk background.
 * @property {string}   background_url - URL to the kiosk background image.
 * @property {boolean}  dark_mode - Whether the kiosk is closer to the dark theme (e.g., Remastered) or light theme (e.g., Rembrandts Amsterdam Experience).
 *
 * @property {number}   font_size - Relative font size as a percentage of the default.
 * @property {string}   font_color - Hex color for text in the kiosk.
 * @property {string}   font_regular_url - URL to the regular font file.
 * @property {string}   font_headers_url - URL to the header font file.
 * @property {number}   font_shadow_size - Size of the font shadow (in pixels).
 * @property {number}   font_shadow_opacity - Opacity of the font shadow (0-100).
 *
 * @property {string}   button_background_color - Hex color code for the button background.
 * @property {string}   button_font_color - Hex color code for the button font color.
 * @property {boolean}  button_shadows - Determines if the button has a shadow effect.
 * @property {number}   button_rounding - Button rounding percentage (0-100).
 *
 * @property {string}   popup_background_color - Hex color code for the popup background.
 * @property {string}   popup_font_color - Hex color code for the popup font color.
 *
 * @property {string}   sample_photo_url - URL to a sample photo used on the Home screen.
 * @property {string}   sample_portrait_url - URL to a sample portrait used on the Home screen.
 * @property {?string}  splashscreen_video_url - URL to the Home screen splash screen video.
 * @property {?string}  splashscreen_background_url - URL to a static background image for the Home screen (shown behind content).
 * @property {import('@/shared/types/api').AttractExperimentConfig|null} [attract_experiment] - Attract-loop A/B test for this kiosk; null when none is running.
 *
 * @property {string[]} languages - Supported languages in ISO codes, e.g., ['en-gb', 'nl'].
 *
 * @property {string[]} portrait_style_ids - Array of available portrait styles IDs (from prompt_styles table)
 * @property {boolean}  [portrait_styles_enable_selector] - Whether to show the portrait style selector UI to users.
 * @property {string}   portrait_s3_bucket_name - S3 bucket name for storing portrait images.
 * @property {string}   portrait_priority_1st - Priority for the first portrait generation request.
 * @property {string}   portrait_priority_default - Priority for the default portrait generation request.
 * @property {'legacy'|'pages'|'strip'|'decks'|'focus'|'zones'|'feed'|null} [classic_gallery_layout] - Classic portrait gallery: 'pages' keeps the old carousel/grid; 'strip' (one row), 'decks' (groups over singles), 'focus' (stage + dock), 'zones' (two scroll zones) and 'feed' (vertical feed) are the mixed layouts; 'legacy' falls back to the pre-mixed-gallery components. Null = the build default.
 *
 * @property {number}   price - Price of the first copy of each portrait.
 * @property {number}   [price_multi] - Price of the first copy of each group portrait. Empty = the price of a single portrait.
 * @property {number}   [price_multi_copy] - Price for additional copies (2nd copy onwards).
 * @property {string}   currency - Currency code used in the kiosk, e.g., 'eur'.
 * @property {boolean}  is_stripe_terminal_payment - Lead customer to Stripe payment
 * @property {'eu_policy'|'us_policy'|'uk_policy'|'australia_policy'|'singapore_policy'|'br_policy'|'ca_policy'|'ch_policy'|'mx_policy'|'pe_policy'|'us_policy_popup'} privacy_policy_variant - Privacy policy variant
 * @property {boolean}  [show_privacy_popup] - Whether to gate the user with a GDPR consent popup. If null, falls back to !!privacy_policy_variant.
 * @property {number|null} [policy_profile_version_id] - Version row id of the editable privacy profile shown to the user.
 * @property {Object.<string, KioskPolicyContent>|null} [policy_content] - Localized editable privacy profile content.
 * @property {import('@/shared/types/api').PrintLogoConfig|null} [print_logo_config] - Branding applied to printed photos.
 *
 * @property {?string[]} demographics_confirmations - Which demographics selectors to show on the face selection screen.
 *   Array of field names, e.g. ["gender", "age_group"]. When null or empty, no selectors are shown (trust AI detection).
 * @property {?boolean}  show_nonbinary_gender - Whether to include the "Non-Binary" option in the gender selector. Defaults to false when null.
 * @property {?boolean}  [multi_subject_enabled] - Whether a session of two or more people gets a group portrait. A missing or null value means no.
 *
 * @property {number}   max_idle_duration
 * @property {number}   max_idle_popup_duration
 *
 * @property {boolean}  [gophoto_printing] - Whether this kiosk uses GoPhoto for photo delivery (skips payment flow)
 */

export class KioskDesign {
  /**
   * Kiosk configuration. null when not loaded or after reset.
   * @type {Kiosk|null}
   */
  @reactive
  kiosk = null;

  /**
   * Partner configuration. null when not loaded or after reset.
   * @type {Object|null}
   */
  @reactive
  partner = null;

  /**
   * Generated CSS code for custom fonts. null when not loaded or after reset.
   * @type {string|null}
   */
  @reactive
  fontFaceCssCode = null;

  constructor() {
    exposeToGlobalScope(this, 'KioskDesign');
  }

  /** @param {import('@/shared/types/api').KioskResponse} kiosk */
  applyKiosk(kiosk) {
    sentryService.addBreadcrumb('kiosk.design', 'Kiosk configuration loaded', {
      slug: kiosk.slug,
      partner: kiosk.partner,
      dark_mode: kiosk.dark_mode,
      languages_count: kiosk.languages?.length,
      styles_count: kiosk.portrait_style_ids?.length,
    });

    // Clear old CSS variables before setting new ones to prevent
    // stale values from a previous kiosk config leaking through.
    this.clearCssVariables();

    this.kiosk = kiosk;
    this.fontFaceCssCode = this.generateFontFaceCssCode(kiosk);
    this.setCssVariables(kiosk);
    this.setPortraitOrientationClass(kiosk);

    sentryService.addBreadcrumb(
      'kiosk.design',
      'CSS variables and fonts applied',
      {
        has_custom_header_font: !!kiosk.font_headers_url,
        has_custom_regular_font: !!kiosk.font_regular_url,
        has_background_image: !!kiosk.background_url,
      }
    );

    this._setKioskSentryContext(kiosk);
  }

  setCssVariables(kiosk) {
    setCssVariable('--Kiosk--font_size', kiosk.font_size || 100, '%');
    setCssVariable('--Kiosk--font_color', kiosk.font_color || '#ffffff');
    setCssVariable(
      '--Kiosk--font_color-rgb',
      colorToRgba(kiosk.font_color, { short: true }) || '255, 255, 255'
    );

    setCssVariable(
      '--Kiosk--font_shadow_size',
      (kiosk.font_shadow_size || 0) / 100
    );
    setCssVariable(
      '--Kiosk--font_shadow_opacity',
      (kiosk.font_shadow_opacity || 0) / 100
    );

    // Scale logo sizes for v2 layout without mutating the reactive config object.
    // Mutating in-place would cause compounding shrinkage if setCssVariables is ever called twice.
    // todo: reduce logo sizes manually in DB after migrated all kiosks to v2
    const logoSize = kiosk.logo_size * 0.75;
    const logoOffsetX = kiosk.logo_offset_x * 0.75;
    const logoOffsetY = kiosk.logo_offset_y * 0.75;

    setCssVariable('--Kiosk--logo_size', logoSize, 'rem');
    setCssVariable('--Kiosk--logo_offset_x', logoOffsetX, 'rem');
    setCssVariable('--Kiosk--logo_offset_y', logoOffsetY, 'rem');
    if (kiosk.logo_url) {
      setCssVariable('--Kiosk--logo_url', `url("${kiosk.logo_url}")`);
    }

    // Button colors: use font_color/background_color as defaults if not specified
    const buttonBgColor =
      kiosk.button_background_color || kiosk.font_color || '#ffffff';
    const buttonFontColor =
      kiosk.button_font_color || kiosk.background_color || '#000000';
    setCssVariable('--Kiosk--button_background_color', buttonBgColor);
    setCssVariable('--Kiosk--button_font_color', buttonFontColor);
    setCssVariable(
      '--Kiosk--button_rounding',
      ((kiosk.button_rounding || 0) * 1.3) / 100,
      'em'
    ); // 1.3em being approximately 100% rounding, see https://stackoverflow.com/a/76634280/4373597

    // Popup colors: use button colors as defaults if not specified
    const popupBgColor = kiosk.popup_background_color || buttonBgColor;
    const popupFontColor = kiosk.popup_font_color || buttonFontColor;
    setCssVariable('--Kiosk--popup_background_color', popupBgColor);
    setCssVariable('--Kiosk--popup_font_color', popupFontColor);

    setCssVariable(
      '--Kiosk--background_color',
      kiosk.background_color || '#000000'
    );
    setCssVariable(
      '--Kiosk--background_color-rgb',
      colorToRgba(kiosk.background_color, { short: true }) || '0, 0, 0'
    );
    if (kiosk.background_url) {
      setCssVariable(
        '--Kiosk--background_url',
        `url("${kiosk.background_url}")`
      );
    }

    let tintColor = '#000000';
    let tintColorRgba = '0, 0, 0';
    let tintInverseColor = '#FFFFFF';
    let tintInverseColorRgba = '255, 255, 255';

    if (kiosk.dark_mode) {
      tintColor = '#FFFFFF';
      tintColorRgba = '255, 255, 255';
      tintInverseColor = '#000000';
      tintInverseColorRgba = '0, 0, 0';
    }

    setCssVariable('--Kiosk--tint-color', tintColor);
    setCssVariable('--Kiosk--tint-color-rgb', tintColorRgba);
    setCssVariable('--Kiosk--tint-inverse-color', tintInverseColor);
    setCssVariable('--Kiosk--tint-inverse-color-rgb', tintInverseColorRgba);
    setCssVariable('--Kiosk--tint-inverse_color', tintInverseColor);
    setCssVariable('--Kiosk--tint-inverse_color-rgb', tintInverseColorRgba);

    if (kiosk.portrait_no_photo_icon_size != null) {
      setCssVariable(
        '--Kiosk--portrait_no_photo_icon_size',
        kiosk.portrait_no_photo_icon_size
      );
    }

    // Text capitalization: 'uppercase' when enabled (default), 'none' when disabled
    setCssVariable(
      '--Kiosk--font_capitalize',
      kiosk.font_capitalize === false ? 'none' : 'uppercase'
    );
  }

  setPortraitOrientationClass(kiosk) {
    const root = document.querySelector(':root');
    if (!root) {
      return;
    }
    const portraitOrientation = resolvePortraitOrientation(kiosk);
    root.setAttribute(PORTRAIT_ORIENTATION_ATTRIBUTE, portraitOrientation);
    root.classList.toggle(
      HORIZONTAL_PORTRAITS_CLASS,
      portraitOrientation === 'horizontal'
    );
  }

  clearPortraitOrientationClass() {
    const root = document.querySelector(':root');
    root?.classList.remove(HORIZONTAL_PORTRAITS_CLASS);
    root?.removeAttribute(PORTRAIT_ORIENTATION_ATTRIBUTE);
  }

  /**
   * Remove all --Kiosk-- CSS custom properties from document.documentElement.
   * Called before re-applying to prevent stale values from a previous kiosk
   * config leaking through (e.g., old logo_url persisting when new config
   * has no logo, or old font family names persisting after reset).
   */
  clearCssVariables() {
    const el = document.documentElement;
    for (const name of ALL_KIOSK_CSS_VARIABLES) {
      el.style.removeProperty(name);
    }
    this.clearPortraitOrientationClass();
  }

  generateFontFaceCssCode(kiosk) {
    const fontPrimary = this.generateFontFaceRule(kiosk.font_headers_url);
    const fontSecondary = this.generateFontFaceRule(kiosk.font_regular_url);
    let fontFaceCssCode = '';

    if (fontPrimary) {
      fontFaceCssCode += fontPrimary.cssCode;
      setCssVariable(
        '--Kiosk--font_headers_url',
        `"${fontPrimary.fontName}", var(--Global--font)`
      );
    }
    if (fontSecondary) {
      fontFaceCssCode += fontSecondary.cssCode;
      setCssVariable(
        '--Kiosk--font_regular_url',
        `"${fontSecondary.fontName}", var(--Global--font)`
      );
    }

    // Fallback: if only one font is provided, use it for both header and regular.
    // Only set the CSS variable for the MISSING font -- @font-face was already emitted above.
    if (!fontPrimary && fontSecondary) {
      setCssVariable(
        '--Kiosk--font_headers_url',
        `"${fontSecondary.fontName}", var(--Global--font)`
      );
    }
    if (!fontSecondary && fontPrimary) {
      setCssVariable(
        '--Kiosk--font_regular_url',
        `"${fontPrimary.fontName}", var(--Global--font)`
      );
    }
    return `<style>${fontFaceCssCode}</style>`;
  }

  generateFontFaceRule(fontUrl) {
    if (!fontUrl) {
      return null;
    }
    const fontFormatsDict = {
      ttf: 'truetype',
      otf: 'opentype',
      woff: 'woff',
      woff2: 'woff2',
      svg: 'svg',
    };
    const fileName = getFileNameFromUrl(fontUrl);
    if (!(fileName && fileName.includes('.'))) {
      console.warn('[KioskDesign] Font URL has no file extension:', fontUrl);
      return null;
    }

    // Use lastIndexOf to handle filenames with multiple dots (e.g. "MyFont.Regular.woff2").
    // The font name is everything before the last dot; the extension is after it.
    const lastDotIndex = fileName.lastIndexOf('.');
    const fontName = fileName.slice(0, lastDotIndex);
    const fontExtension = fileName.slice(lastDotIndex + 1);

    const fontFormat = fontFormatsDict[fontExtension.toLowerCase()];
    if (!fontFormat) {
      console.warn(
        `[KioskDesign] Unknown font extension "${fontExtension}" from URL:`,
        fontUrl
      );
      return null;
    }

    const cssCode = `
      @font-face {
        font-family: "${fontName}";
        font-weight: normal;
        font-style: normal;
        font-display: block;
        src: url("${fontUrl}") format("${fontFormat}");
      }
    `;
    return { fontName, cssCode };
  }

  /**
   * Set comprehensive kiosk configuration context in Sentry.
   * This context will be attached to all subsequent error reports and events.
   *
   * Uses sentryService wrapper methods which include try-catch protection
   * and initialization checks. Sensitive data (AWS bucket names, pricing)
   * is intentionally excluded to avoid data leakage.
   *
   * @param {Kiosk} kiosk - The loaded kiosk configuration
   * @private
   */
  _setKioskSentryContext(kiosk) {
    // Set kiosk context with non-sensitive configuration details
    sentryService.setContext('kiosk_config', {
      slug: kiosk.slug,
      partner: kiosk.partner,
      printer: kiosk.printer,
      client_name: kiosk.client_name,

      // Visual configuration
      dark_mode: kiosk.dark_mode,
      has_custom_logo: !!kiosk.logo_url,
      has_background_image: !!kiosk.background_url,
      has_custom_header_font: !!kiosk.font_headers_url,
      has_custom_regular_font: !!kiosk.font_regular_url,

      // Portrait configuration (no S3 bucket - sensitive infrastructure)
      portrait_styles_count: kiosk.portrait_style_ids?.length || 0,
      portrait_style_ids: kiosk.portrait_style_ids?.slice(0, 5), // First 5 for reference

      // Pricing: boolean indicators only, not actual values
      has_pricing: !!kiosk.price,
      has_multi_copy_pricing: !!kiosk.price_multi_copy,
      currency: kiosk.currency,
      payment_enabled: !!kiosk.is_stripe_terminal_payment,

      // Demographics configuration
      demographics_confirmations: kiosk.demographics_confirmations,
      show_nonbinary_gender: kiosk.show_nonbinary_gender,

      // Localization
      languages: kiosk.languages,
      privacy_policy_variant: kiosk.privacy_policy_variant,
      show_privacy_popup: kiosk.show_privacy_popup,

      // Timeouts
      max_idle_duration: kiosk.max_idle_duration,
      max_idle_popup_duration: kiosk.max_idle_popup_duration,
    });

    // Set searchable tags for filtering in Sentry dashboard
    sentryService.setTag('kiosk_slug', kiosk.slug);
    sentryService.setTag('kiosk_partner', kiosk.partner);
    sentryService.setTag('kiosk_dark_mode', kiosk.dark_mode ? 'true' : 'false');
    sentryService.setTag('kiosk_currency', kiosk.currency);
  }

  /**
   * Clear the kiosk configuration context and tags from Sentry.
   * Must clear all tags set by _setKioskSentryContext() to prevent
   * stale values from leaking into subsequent events on a 24/7 kiosk.
   */
  clearSentryContext() {
    sentryService.setContext('kiosk_config', null);
    // Clear all tags set by _setKioskSentryContext()
    sentryService.setTag('kiosk_slug', undefined);
    sentryService.setTag('kiosk_partner', undefined);
    sentryService.setTag('kiosk_dark_mode', undefined);
    sentryService.setTag('kiosk_currency', undefined);
  }

  /**
   * Reset the KioskDesign service state.
   * Clears Sentry context, removes all CSS variables, and resets internal state.
   */
  reset() {
    this.clearSentryContext();
    this.clearCssVariables();
    this.kiosk = null;
    this.partner = null;
    this.fontFaceCssCode = null;
  }
}

export const kioskDesign = new KioskDesign();
